/**
 * Local sample free-agent pool for the League Players page (fixtures mode).
 *
 * Generates a deterministic, realistic set of players — mix of hitters and
 * pitchers with plausible season stats — so the Players page renders a full,
 * sortable, filterable list without hitting the API. Shapes each player the way
 * LeaguePlayers expects: numeric `stats` (avg/hr/rbi/r/sb, era/whip/k/w/sv),
 * an `isPitcher` flag, eligible positions, team, and derived hits/atBats.
 */

const FIRST_NAMES = [
  'Marcus', 'Diego', 'Hideki', 'Owen', 'Theo', 'Jaylen', 'Santino', 'Emilio', 'Quentin', 'Isaiah',
  'Cole', 'Andre', 'Raymond', 'Victor', 'Sung-min', 'Mateo', 'Elias', 'Rafael', 'Kenji', 'Dominic',
  'Luis', 'Gabriel', 'Noah', 'Silas', 'Amir', 'Bryce', 'Caleb', 'Dashawn', 'Enrique', 'Felix',
  'Grant', 'Hunter', 'Ivan', 'Jonah', 'Kai', 'Leo', 'Miles', 'Nolan', 'Omar', 'Preston',
  'Rhys', 'Tobias', 'Ulises', 'Wesley', 'Xavier', 'Yusuke', 'Zane', 'Andres', 'Beau', 'Cormac',
];
const LAST_NAMES = [
  'Willis', 'Bannister', 'Mbeki', 'Perez', 'Sato', 'Foster', 'Davies', 'Rodriguez', 'Brooks', 'Kim',
  'Castellano', 'Lindqvist', 'Vargas', 'Rossi', 'Navarro', 'Okafor', 'Nakamura', 'Delgado', 'Fitzgerald', 'Moreau',
  'Petrov', 'Hendricks', 'Ishikawa', 'Alvarado', 'Bello', 'Contreras', 'Dubois', 'Espinoza', 'Farrell', 'Guerrero',
  'Haas', 'Ibarra', 'Jensen', 'Kowalski', 'Larsson', 'Montero', 'Nystrom', 'Ortiz', 'Paredes', 'Quintero',
  'Reyes', 'Sandoval', 'Takahashi', 'Ueda', 'Vidal', 'Whitaker', 'Yamamoto', 'Zamora', 'Adeyemi', 'Bianchi',
];
const MLB_TEAMS = [
  'NYY', 'BOS', 'TB', 'TOR', 'BAL', 'CLE', 'DET', 'KC', 'MIN', 'CWS',
  'HOU', 'LAA', 'OAK', 'SEA', 'TEX', 'ATL', 'MIA', 'NYM', 'PHI', 'WSH',
  'CHC', 'CIN', 'MIL', 'PIT', 'STL', 'ARI', 'COL', 'LAD', 'SD', 'SF',
];
const HITTER_POSITIONS = ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF'];

// Deterministic seeded RNG so the pool is stable across renders/sorts.
function rand(seed) {
  const x = Math.sin(seed * 12.9898 + 4.1414) * 43758.5453;
  return x - Math.floor(x);
}
const round = (n, d = 0) => {
  const f = 10 ** d;
  return Math.round(n * f) / f;
};

function makeHitter(i) {
  const first = FIRST_NAMES[Math.floor(rand(i * 3.1) * FIRST_NAMES.length)];
  const last = LAST_NAMES[Math.floor(rand(i * 7.7 + 1) * LAST_NAMES.length)];
  const team = MLB_TEAMS[Math.floor(rand(i * 2.3 + 2) * MLB_TEAMS.length)];
  const primary = HITTER_POSITIONS[Math.floor(rand(i * 5.9 + 3) * HITTER_POSITIONS.length)];
  const isOF = ['LF', 'CF', 'RF'].includes(primary);
  const positions = isOF ? [primary, 'OF', 'UTIL'] : [primary, 'UTIL'];

  const avg = round(0.225 + rand(i * 1.7 + 4) * 0.11, 3);
  const hr = Math.floor(rand(i * 4.2 + 5) * 38) + 2;
  const rbi = Math.floor(rand(i * 6.1 + 6) * 78) + 18;
  const r = Math.floor(rand(i * 8.3 + 7) * 74) + 20;
  const sb = Math.floor(rand(i * 9.4 + 8) * 34);
  const atBats = 340 + Math.floor(rand(i * 3.5 + 9) * 240);
  const hits = Math.round(avg * atBats);

  return {
    id: `fa-h-${i}`,
    playerId: `fa-h-${i}`,
    name: `${first} ${last}`,
    firstName: first,
    lastName: last,
    position: primary,
    positions,
    eligiblePositions: positions,
    team,
    teamId: null,
    photoUrl: null,
    status: 'active',
    isPitcher: false,
    stats: { avg, hr, rbi, r, sb, hits, atBats, era: 0, whip: 0, k: 0, w: 0, sv: 0 },
  };
}

function makePitcher(i) {
  const first = FIRST_NAMES[Math.floor(rand(i * 3.7 + 100) * FIRST_NAMES.length)];
  const last = LAST_NAMES[Math.floor(rand(i * 6.3 + 101) * LAST_NAMES.length)];
  const team = MLB_TEAMS[Math.floor(rand(i * 2.9 + 102) * MLB_TEAMS.length)];
  const isReliever = rand(i * 5.1 + 103) > 0.62;
  const primary = isReliever ? 'RP' : 'SP';

  const era = round(2.55 + rand(i * 1.9 + 104) * 2.4, 2);
  const whip = round(1.02 + rand(i * 4.7 + 105) * 0.42, 2);
  const k = isReliever
    ? Math.floor(rand(i * 6.9 + 106) * 55) + 45
    : Math.floor(rand(i * 6.9 + 106) * 150) + 90;
  const w = isReliever ? Math.floor(rand(i * 8.1 + 107) * 7) + 1 : Math.floor(rand(i * 8.1 + 107) * 15) + 3;
  const sv = isReliever && rand(i * 9.2 + 108) > 0.45 ? Math.floor(rand(i * 2.6 + 109) * 38) + 2 : 0;

  return {
    id: `fa-p-${i}`,
    playerId: `fa-p-${i}`,
    name: `${first} ${last}`,
    firstName: first,
    lastName: last,
    position: primary,
    positions: [primary, 'P'],
    eligiblePositions: [primary, 'P'],
    team,
    teamId: null,
    photoUrl: null,
    status: 'active',
    isPitcher: true,
    stats: { era, whip, k, w, sv, avg: 0, hr: 0, rbi: 0, r: 0, sb: 0, hits: 0, atBats: 0 },
  };
}

let POOL = null;
function buildPool() {
  const hitters = Array.from({ length: 100 }, (_, i) => makeHitter(i));
  const pitchers = Array.from({ length: 64 }, (_, i) => makePitcher(i));
  // Interleave so the default list is a natural mix of hitters and pitchers.
  const out = [];
  const max = Math.max(hitters.length, pitchers.length);
  for (let i = 0; i < max; i++) {
    if (hitters[i]) out.push(hitters[i]);
    if (i % 2 === 0 && pitchers[Math.floor(i / 2)]) out.push(pitchers[Math.floor(i / 2)]);
  }
  return out;
}

export function getSampleFreeAgents() {
  if (!POOL) POOL = buildPool();
  return POOL;
}

// Distinct MLB team abbreviations present in the pool (for the Team filter).
export function getSampleTeams() {
  return [...MLB_TEAMS].sort();
}

// ---- Sample minor-league stat lines (shared by the player profile + Farm) ----
function hashStr(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
function srand(s) {
  const x = Math.sin(hashStr(s) + 1) * 10000;
  return x - Math.floor(x);
}
const fmtAvg = (n) => n.toFixed(3).replace(/^0/, '');

/**
 * A few deterministic minor-league season/level rows for a player. Hitters get a
 * batting line, pitchers a pitching line. Used by the profile's Minor Leagues
 * tab and the Farm page.
 */
export function getSampleMinorLeagueStats({ id, isPitcher, org = 'FA' } = {}) {
  const key = String(id ?? 'x');
  const levels = [
    { season: 2025, level: 'AAA' },
    { season: 2025, level: 'AA' },
    { season: 2024, level: 'A+' },
  ];
  return levels.map(({ season, level }) => {
    const k = `${key}${season}${level}`;
    const team = `${org} ${level}`;
    if (isPitcher) {
      const ip = Math.round((45 + srand(k + 'ip') * 85) * 10) / 10;
      return {
        season, level, team,
        W: Math.floor(srand(k + 'w') * 10) + 1,
        L: Math.floor(srand(k + 'l') * 8),
        SV: srand(k + 'sv') > 0.72 ? Math.floor(srand(k + 'svn') * 14) + 1 : 0,
        ERA: (2.20 + srand(k + 'era') * 3).toFixed(2),
        WHIP: (0.95 + srand(k + 'whip') * 0.6).toFixed(2),
        K: Math.floor(ip * (0.9 + srand(k + 'k') * 0.45)),
        IP: ip,
      };
    }
    const AB = Math.floor(180 + srand(k + 'ab') * 320);
    const avgN = 0.235 + srand(k + 'avg') * 0.11;
    return {
      season, level, team,
      G: Math.floor(AB / 3.6),
      AB,
      H: Math.round(AB * avgN),
      AVG: fmtAvg(avgN),
      HR: Math.floor(srand(k + 'hr') * 22),
      RBI: Math.floor(srand(k + 'rbi') * 60) + 12,
      R: Math.floor(srand(k + 'r') * 55) + 12,
      SB: Math.floor(srand(k + 'sb') * 26),
      OPS: fmtAvg(0.680 + srand(k + 'ops') * 0.34),
    };
  });
}
